import i18next from 'i18next';
import {
  BufferGeometry,
  Color,
  Group,
  Line,
  LineBasicMaterial,
  MeshBasicMaterial,
  SRGBColorSpace,
  Vector3,
} from 'three';
import { Text } from 'troika-three-text';

import { roundToDecimals } from '../../utils/math/number';
import { renderOrders } from '../render-orders';
import { radiansToDegrees } from '../utilities/math-helpers';
import { PickingSupportLine } from './picking-support-line';
import { AngleGuide } from './utils/angle-guide';
import { getWorldPerPixel } from './utils/world-per-pixel';

// Orange
const referenceLineColor = new Color(0xff9900);
// Light blue, so that north/south and west/east are not mistaken for an actual line
const axisColor = new Color(0x33bbff);
// Green, like the highlight of the line that is snapped towards, unless the line has its own color
const crossedLineColor = new Color(0, 1, 0);
/** How much lighter the support line is than a line with its own color, towards white. */
const crossedLineLightening = 0.35;

const arcRadiusPx = 40;
/**
 * The arc always has the same number of segments, since the geometry reuses its buffer
 * and can not change the number of points.
 */
const arcSegments = 32;
const angleLabelDistancePx = arcRadiusPx + 18;
const axisLabelDistancePx = 100;
const labelSizePx = 16;
/** Angles (radians) below this have no arc or label, since the line runs along the reference line. */
const minAngle = 1e-4;

/**
 * Lightens the color towards white, in sRGB so that it looks as much lighter for dark colors as
 * for light ones.
 */
export const lighten = (color: Color, amount: number) => {
  const { r, g, b } = color.getRGB({ r: 0, g: 0, b: 0 }, SRGBColorSpace);
  const towardsWhite = (value: number) => value + (1 - value) * amount;

  return new Color().setRGB(towardsWhite(r), towardsWhite(g), towardsWhite(b), SRGBColorSpace);
};

const labelMaterial = new MeshBasicMaterial({
  transparent: true,
  depthTest: false,
  depthWrite: false,
  toneMapped: false,
});

/**
 * A line that the snapped line ends on, with the color of the line if it is extended in it, e.g.
 * for the line of an asset. The support line is a bit lighter.
 */
export type CrossedLine = { start: Vector3; end: Vector3; color?: Color };

/**
 * Visualizes a line that is snapped to an angle. The reference line that the angle is measured from
 * crosses the whole screen, and an arc with a label shows the angle between it and the line.
 * If the line ends where it crosses another line, that line also crosses the whole screen.
 *
 * If there is no reference line, the north/south or west/east axis is shown in another color,
 * with labels for the directions.
 */
export class PickingAngle extends Group {
  /** The guide that is shown, if any. */
  guide?: AngleGuide;
  /** The line that the snapped line ends on, if any. */
  crossedLine?: CrossedLine;

  private referenceLine = this.createSupportLine(referenceLineColor);
  private crossedSupportLine = this.createSupportLine(crossedLineColor);
  /** Arc with a radius of 1, scaled to the same size on screen before it is rendered. */
  private arc = new Line(
    new BufferGeometry(),
    new LineBasicMaterial({ depthTest: false, depthWrite: false, transparent: true }),
  );

  private origin = new Vector3();
  // Label positions relative to the origin, in pixels.
  private angleLabelOffset = new Vector3();
  private positiveAxisLabelOffset = new Vector3();
  private negativeAxisLabelOffset = new Vector3();

  private angleLabel = this.createLabel(this.angleLabelOffset);
  private positiveAxisLabel = this.createLabel(this.positiveAxisLabelOffset);
  private negativeAxisLabel = this.createLabel(this.negativeAxisLabelOffset);

  constructor() {
    super();

    this.visible = false;

    this.arc.renderOrder = renderOrders.pickers.angleGuideLabel;
    this.arc.frustumCulled = false;
    this.arc.onBeforeRender = (renderer, scene, camera) => {
      this.arc.scale.setScalar(arcRadiusPx * getWorldPerPixel(renderer, camera, this.origin));
      this.arc.updateMatrixWorld();
    };

    this.add(
      this.referenceLine,
      this.crossedSupportLine,
      this.arc,
      this.angleLabel,
      this.positiveAxisLabel,
      this.negativeAxisLabel,
    );
  }

  /**
   * Creates a dashed line, with the same size of the dashes on screen as at the origin.
   */
  private createSupportLine(color: Color) {
    return new PickingSupportLine(color, renderOrders.pickers.angleGuideLine, () => this.origin);
  }

  /**
   * Creates a label that faces the camera, with the same size on screen and placed at the offset
   * (in pixels) from the origin.
   */
  private createLabel(offset: Vector3) {
    const text = new Text();
    text.fontSize = 1;
    text.fontWeight = 'bold';
    text.anchorX = 'center';
    text.anchorY = 'middle';
    text.outlineColor = 0x000000;
    text.outlineOpacity = 0.8;
    text.outlineWidth = '15%';
    text.material = labelMaterial;
    text.renderOrder = renderOrders.pickers.angleGuideLabel;
    text.frustumCulled = false;

    const troikaOnBeforeRender = text.onBeforeRender.bind(text);

    text.onBeforeRender = (renderer, scene, camera, geometry, material, group) => {
      troikaOnBeforeRender(renderer, scene, camera, geometry, material, group);

      const worldPerPixel = getWorldPerPixel(renderer, camera, this.origin);

      text.position.copy(offset).multiplyScalar(worldPerPixel).add(this.origin);
      camera.getWorldQuaternion(text.quaternion);
      text.scale.setScalar(labelSizePx * worldPerPixel);
      text.updateMatrixWorld();
    };

    return text;
  }

  /**
   * @param crossedLine - The line that the snapped line ends on, if it ends where it crosses a line.
   */
  show(guide: AngleGuide, crossedLine?: CrossedLine) {
    const { origin, referenceLine, axis, from, angle } = guide;
    const color = axis ? axisColor : referenceLineColor;

    this.guide = guide;
    this.crossedLine = crossedLine;
    this.visible = true;
    this.origin.copy(origin);

    this.referenceLine.setColor(color);
    this.referenceLine.place(referenceLine.point, referenceLine.direction);

    this.crossedSupportLine.visible = Boolean(crossedLine);

    if (crossedLine) {
      this.crossedSupportLine.setColor(
        crossedLine.color ? lighten(crossedLine.color, crossedLineLightening) : crossedLineColor,
      );
      this.crossedSupportLine.place(
        crossedLine.start,
        crossedLine.end.clone().sub(crossedLine.start),
      );
    }

    const hasAngle = Math.abs(angle) > minAngle;
    this.arc.visible = hasAngle;
    this.angleLabel.visible = hasAngle;

    if (hasAngle) {
      const start = from.angle();

      this.arc.position.copy(origin);
      this.arc.geometry.setFromPoints(
        Array.from({ length: arcSegments + 1 }, (_, i) => {
          const a = start + (angle * i) / arcSegments;
          return new Vector3(Math.cos(a), Math.sin(a), 0);
        }),
      );
      this.arc.material.color = color;

      const bisector = start + angle / 2;
      this.angleLabelOffset.set(Math.cos(bisector), Math.sin(bisector), 0);
      this.angleLabelOffset.multiplyScalar(angleLabelDistancePx);
      this.angleLabel.text = `${roundToDecimals(radiansToDegrees(Math.abs(angle)), 2)}°`;
      this.angleLabel.color = color;
    }

    this.positiveAxisLabel.visible = Boolean(axis);
    this.negativeAxisLabel.visible = Boolean(axis);

    if (axis) {
      this.positiveAxisLabelOffset
        .copy(referenceLine.direction)
        .multiplyScalar(axisLabelDistancePx);
      this.negativeAxisLabelOffset.copy(this.positiveAxisLabelOffset).negate();

      [this.positiveAxisLabel.text, this.negativeAxisLabel.text] =
        axis === 'northSouth'
          ? [
              i18next.t('snap.compass.north', { ns: 'skyviewDesign' }),
              i18next.t('snap.compass.south', { ns: 'skyviewDesign' }),
            ]
          : [
              i18next.t('snap.compass.east', { ns: 'skyviewDesign' }),
              i18next.t('snap.compass.west', { ns: 'skyviewDesign' }),
            ];
      this.positiveAxisLabel.color = color;
      this.negativeAxisLabel.color = color;
    }
  }

  hide() {
    this.guide = undefined;
    this.crossedLine = undefined;
    this.visible = false;
  }
}
